import { Global, css } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import { useQuery } from '@apollo/client';

import { DOMParser } from 'js/lib/dom';

import BatchTranslateHtmlQuery from 'bundles/ondemand-translation/api/BatchTranslateHtmlQuery.graphql';
import type {
  TranslateContent_BatchTranslateHtmlQuery as BatchTranslateHtmlQueryResult,
  TranslateContent_BatchTranslateHtmlQueryVariables as BatchTranslateHtmlQueryVariables,
} from 'bundles/ondemand-translation/api/__generated__/BatchTranslateHtmlQuery';
import type { TranslatedContentId } from 'bundles/ondemand-translation/components/TranslationContextProvider';
import { TranslationContext } from 'bundles/ondemand-translation/components/TranslationContextProvider';

type TranslatedDangerouslySetHtmlProps = {
  enableTranslation?: boolean;
  html: string;
};
const styles = css`
  .TranslatedDangerouslySetHtml {
    a {
      text-decoration: underline;

      &:hover {
        text-decoration: none;
      }
    }
  }
`;
/**
 * This component gets a html content as an input and calls translation API to return translated content.
 *
 * @param enableTranslation specifies whether the translation feature is enabled.
 * @param html input content
 */
export const TranslatedDangerouslySetHtml = ({ enableTranslation, html }: TranslatedDangerouslySetHtmlProps) => {
  const context = React.useContext(TranslationContext);

  const contentIdRef = useRef<TranslatedContentId | undefined>(undefined);

  useEffect(() => {
    if (!contentIdRef.current && context) {
      contentIdRef.current = context.getTranslatedContentId();
    }
  }, [context]);

  useEffect(() => {
    if (enableTranslation && context && contentIdRef.current) {
      const domParser = new DOMParser();
      const document: Document = domParser.parseFromString(html, 'text/html');
      const textValue = document.documentElement.textContent;
      if (textValue) {
        context.languageDetector.registerOriginalTextValue(contentIdRef.current, textValue);
      }
    }
  }, [context, enableTranslation, html]);

  // Do not translate if input is empty or whitespace - backend returns validation error for empty input
  const shouldTranslate = enableTranslation && context?.showTranslatedContent && html && html.trim();

  const queryVariables = {
    inputs: [html],
    // Pass empty source language as the language detection is more reliable on the backend.
    languageSettings: {},
  };

  const { data, error, refetch, loading } = useQuery<BatchTranslateHtmlQueryResult, BatchTranslateHtmlQueryVariables>(
    BatchTranslateHtmlQuery,
    {
      context: { clientName: 'gatewayGql' },
      skip: !shouldTranslate,
      variables: queryVariables,
      notifyOnNetworkStatusChange: true, // this needs to be enabled, otherwise it will not rerender on refetch
    }
  );

  useEffect(() => {
    if (contentIdRef.current) {
      context?.progressTracker.registerQueryProgress(contentIdRef.current, loading);
    }
  }, [context?.progressTracker, loading]);

  let hasError = error !== undefined;

  let outputContent = html;

  const result = data?.TranslateContent?.batchTranslateHtml;

  useEffect(() => {
    if (contentIdRef.current) {
      if (result?.__typename === 'TranslateContent_BatchTranslateHTMLSuccess' && result.detectedSourceLanguage) {
        context?.languageDetector.registerDetectedLanguage(contentIdRef.current, result.detectedSourceLanguage);
      }
    }
  }, [context?.languageDetector, result]);

  if (result?.__typename === 'TranslateContent_BatchTranslateHTMLSuccess') {
    const translatedData = result.results?.[0];

    if (translatedData?.__typename === 'TranslateContent_TranslateHTMLPayload') {
      outputContent = translatedData.html;
    } else if (translatedData?.__typename === 'TranslateContent_TranslateFailedError') {
      hasError = true;
    }
  } else if (result?.__typename === 'TranslateContent_RateLimitError') {
    hasError = true;
  } else if (result?.__typename === 'TranslateContent_TranslateInvalidArgumentError') {
    hasError = true;
  }

  useEffect(() => {
    // Do not register / unregister error while loading (prevents flickering when refetching)
    if (contentIdRef.current && !loading) {
      if (hasError) {
        context?.progressTracker.registerError(contentIdRef.current, refetch);
      } else {
        context?.progressTracker.unregisterError(contentIdRef.current);
      }
    }
  }, [context?.progressTracker, hasError, loading, refetch]);

  return (
    <>
      <Global styles={styles} />
      {
        // eslint-disable-next-line react/no-danger
        <span className="TranslatedDangerouslySetHtml" dangerouslySetInnerHTML={{ __html: outputContent }} />
      }
    </>
  );
};
